import React, { useRef, useCallback } from 'react';
import { Box, Typography, Slider } from '@mui/material';
import { OpenWith } from '@mui/icons-material';

interface WallpaperPositionPickerProps {
  imageUrl: string;
  position: string;
  onChange: (position: string) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
}

function parsePosition(position: string): { x: number; y: number } {
  const parts = position.split(/\s+/);
  const x = parseFloat(parts[0]) || 50;
  const y = parseFloat(parts[1]) || 50;
  return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
}

const WallpaperPositionPicker: React.FC<WallpaperPositionPickerProps> = ({
  imageUrl,
  position,
  onChange,
  zoom,
  onZoomChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef(parsePosition(position));

  // Keep ref in sync with prop
  positionRef.current = parsePosition(position);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      draggingRef.current = true;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return;
      const el = containerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const dx = e.clientX - lastPointerRef.current.x;
      const dy = e.clientY - lastPointerRef.current.y;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };

      // Dragging the image left means we want to see further right → increase X%
      // Sensitivity: dragging across the full container width spans ~100%
      const sensitivity = 1.5;
      const { x, y } = positionRef.current;
      const newX = Math.max(0, Math.min(100, x - (dx / rect.width) * 100 * sensitivity));
      const newY = Math.max(0, Math.min(100, y - (dy / rect.height) * 100 * sensitivity));

      onChange(`${Math.round(newX)}% ${Math.round(newY)}%`);
    },
    [onChange],
  );

  const handlePointerUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Drag to reposition the image
      </Typography>

      <Box
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        sx={{
          position: 'relative',
          width: '100%',
          height: 180,
          borderRadius: 1.5,
          overflow: 'hidden',
          cursor: 'grab',
          border: 1,
          borderColor: 'divider',
          userSelect: 'none',
          touchAction: 'none',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        {(() => {
          const p = parsePosition(position);
          return (
            <Box
              component="img"
              src={imageUrl}
              alt="Drag to reposition"
              sx={{
                position: 'absolute',
                left: `${p.x}%`,
                top: `${p.y}%`,
                transform: `translate(-${p.x}%, -${p.y}%)`,
                width: `${100 * zoom}%`,
                height: 'auto',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
          );
        })()}
        {/* Drag hint overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.15)',
            opacity: draggingRef.current ? 0 : 1,
            transition: 'opacity 0.2s',
            pointerEvents: 'none',
            '&:hover': { opacity: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              fontSize: '0.8125rem',
            }}
          >
            <OpenWith sx={{ fontSize: 18 }} />
            Drag to reposition
          </Box>
        </Box>
      </Box>

      {/* Zoom slider */}
      <Box sx={{ mt: 2, px: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Zoom: {zoom === 1 ? 'Original' : `${zoom.toFixed(2).replace(/\.?0+$/, '')}×`}
        </Typography>
        <Slider
          value={zoom}
          min={0.25}
          max={3}
          step={0.05}
          onChange={(_e, val) => onZoomChange(val as number)}
          marks={[
            { value: 0.25, label: '¼×' },
            { value: 0.5, label: '½×' },
            { value: 1, label: 'Original' },
            { value: 2, label: '2×' },
            { value: 3, label: '3×' },
          ]}
          size="small"
        />
      </Box>
    </Box>
  );
};

export default WallpaperPositionPicker;
